import { forwardRef } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { getInitials } from "@acto/taskpane/field-sales-2/utils/strings";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";

const Outer = styled("div")<{ interactive: boolean }>(({ theme }) => ({
  width: "var(--avatar-size)",
  height: "var(--avatar-size)",
  padding: 0,
  margin: 0,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  variants: [
    {
      props: { interactive: true },
      // Shared glass-capsule feel (lift + ring on hover, shrink on press, keyboard focus ring) —
      // the avatar has no resting shadow, so the hover ring stands alone.
      style: { cursor: "pointer", ...glassCapsulePress(theme) },
    },
    { props: { interactive: false }, style: { cursor: "default" } },
  ],
}));

const Ring = styled("span")(({ theme }) => ({
  width: "80%",
  height: "80%",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.avatarRingBorder}`,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const Initials = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[600],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const DEFAULT_AVATAR_SIZE = "3.125rem"; // 50px = theme.fieldSalesTwo.spacing[50]

interface InitialsAvatarProps {
  name: string;
  size?: string;
  onClick?: () => void;
  className?: string;
  /** Marks the avatar as the trigger of a popup of the given type (e.g. a menu). */
  ariaHasPopup?: "menu" | "dialog" | "listbox" | "true";
  /** Reflects whether the popup the avatar controls is currently open. */
  ariaExpanded?: boolean;
}

export const InitialsAvatar = forwardRef<HTMLButtonElement, InitialsAvatarProps>(function InitialsAvatar(
  { name, size = DEFAULT_AVATAR_SIZE, onClick, className, ariaHasPopup, ariaExpanded },
  ref,
) {
  const t = useTranslations("fieldSalesTwo.profile");
  const initials = getInitials(name);
  const sizeStyle = { ["--avatar-size" as string]: size };

  if (onClick) {
    return (
      <Outer
        ref={ref}
        as="button"
        type="button"
        interactive
        onClick={onClick}
        aria-label={name ? t("openOf", { name }) : t("open")}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaHasPopup ? ariaExpanded : undefined}
        className={className}
        style={sizeStyle}
      >
        <Ring>
          <Initials>{initials}</Initials>
        </Ring>
      </Outer>
    );
  }

  // Non-interactive (decorative) variant: renders a div, so the forwarded button ref does not
  // apply here — only the interactive button branch above is anchorable (e.g. as a popover trigger).
  return (
    <Outer
      interactive={false}
      {...(name ? { role: "img", "aria-label": name } : {})}
      className={className}
      style={sizeStyle}
    >
      <Ring>
        <Initials>{initials}</Initials>
      </Ring>
    </Outer>
  );
});
